import { MOVEMENT_KINDS, type MovementKind } from "@bl/domain";

/**
 * Per-device layout, the way the *arr apps do it (manifesto, "Simple"): what you set on your phone stays on
 * your phone, and the desktop keeps its own. It lives in this browser's storage, never on the server, and
 * survives closing the browser. Layouts can be saved under a name, applied, renamed, deleted and reset.
 */

export const INVENTORY_COLUMNS = [
  { id: "name", label: "Name", required: true },
  { id: "form", label: "Form" },
  // Where it is from: a hop's regions on hand, anything else's origin. It replaced Origin so that a
  // layout saved before shows it, since Scott wants a hop's regions in sight (2026-10-02).
  { id: "region", label: "Region" },
  { id: "category", label: "Category" },
  { id: "onHand", label: "On hand" },
  { id: "planned", label: "Planned" },
  // On hand less planned, called Free until Tyler named it Available (2026-10-03).
  { id: "available", label: "Available" },
  { id: "packs", label: "Packs" },
  { id: "locations", label: "Where" },
  { id: "lots", label: "Lots" },
  { id: "avgCost", label: "Avg cost" },
  { id: "value", label: "Value" },
  { id: "manufacturer", label: "Maker" },
  { id: "sku", label: "SKU" },
  { id: "color", label: "Color" },
  { id: "alpha", label: "Alpha" },
  { id: "lastMovement", label: "Last change" },
] as const;
export type InventoryColumnId = (typeof INVENTORY_COLUMNS)[number]["id"];

export const MOVEMENT_COLUMNS = [
  { id: "when", label: "When", required: true },
  { id: "kind", label: "What" },
  { id: "item", label: "Item" },
  { id: "quantity", label: "Amount" },
  { id: "location", label: "Where" },
  { id: "lot", label: "Lot" },
  { id: "entered", label: "As entered" },
  { id: "counterparty", label: "Brewery" },
  { id: "note", label: "Note" },
] as const;
export type MovementColumnId = (typeof MOVEMENT_COLUMNS)[number]["id"];

export const RECIPE_COLUMNS = [
  { id: "name", label: "Name", required: true },
  { id: "style", label: "Style" },
  { id: "type", label: "Type" },
  { id: "batch", label: "Batch" },
  { id: "og", label: "OG" },
  { id: "fg", label: "FG" },
  { id: "abv", label: "ABV" },
  { id: "ibu", label: "IBU" },
  { id: "srm", label: "Color" },
  { id: "stock", label: "Stock" },
  { id: "brewer", label: "Brewer" },
  { id: "version", label: "Version" },
  { id: "lastBrewed", label: "Last brewed" },
  { id: "updated", label: "Last changed" },
] as const;
export type RecipeColumnId = (typeof RECIPE_COLUMNS)[number]["id"];

export interface ColumnSetting<Id extends string> {
  id: Id;
  visible: boolean;
}

export type InventoryView = "table" | "cards" | "overview";
export type SortKey = "name" | "category" | "onHand" | "lastMovement";
export type ColorUnit = "lovibond" | "srm" | "ebc";

/** Recipes, viewed the ways Inventory is (Tyler, 2026-10-01): cards, a list, or a table. */
export type RecipeView = "cards" | "list" | "table";
export const RECIPE_SORT_KEYS = [
  "name",
  "style",
  "batch",
  "og",
  "fg",
  "abv",
  "ibu",
  "srm",
  "lastBrewed",
  "updated",
] as const;
export type RecipeSortKey = (typeof RECIPE_SORT_KEYS)[number];
/** Whether a recipe could be brewed today, as far as its linked lines say. */
export type RecipeStockFilter = "all" | "ready" | "short" | "unlinked";

/**
 * How many days fit across the tank schedule, the stops on its zoom slider (Tyler, 2026-10-05): from
 * a week at its - end to a year at its +, with 2 weeks, a month, a quarter and 6 months between.
 */
export const TANK_ZOOMS = [7, 14, 31, 91, 182, 365] as const;
export type TankZoom = (typeof TANK_ZOOMS)[number];

export interface Layout {
  version: 1;
  inventory: {
    view: InventoryView;
    columns: ColumnSetting<InventoryColumnId>[];
    sort: { key: SortKey; dir: "asc" | "desc" };
    groupByCategory: boolean;
    /** Show quantities in the item's unit (lb) or in packs (bags). */
    quantityMode: "unit" | "pack";
    category: string | null;
    locationId: string | null;
    hideEmpty: boolean;
    cardSize: "small" | "medium" | "large";
    /** Category → folded away under its heading (Tyler, 2026-10-02). */
    collapsed: Record<string, boolean>;
  };
  /**
   * How far To order looks, `ahead` days from today, from a few days to years (Tyler, 2026-10-02):
   * one of its presets, or a Custom date. Kept under the name the Calendar gave it, where To order was a
   * view, so a device keeps its window.
   */
  schedule: { ahead: number; custom: boolean };
  recipes: {
    view: RecipeView;
    columns: ColumnSetting<RecipeColumnId>[];
    sort: { key: RecipeSortKey; dir: "asc" | "desc" };
    groupByStyle: boolean;
    type: string | null;
    style: string | null;
    stock: RecipeStockFilter;
    /** The Favorites section at the top of the list, folded away. */
    favoritesCollapsed: boolean;
  };
  item: {
    /** Section id → collapsed. */
    collapsed: Record<string, boolean>;
  };
  /**
   * Production's tank schedule (Tyler, 2026-10-05): how many days fit across it; whether the tanks
   * hidden from it show anyway, faded (which tanks are hidden is the brewery's, on each tank); and the
   * kinds folded away under their headings.
   */
  production: {
    zoom: TankZoom;
    showHidden: boolean;
    folded: Record<string, boolean>;
  };
  movements: {
    columns: ColumnSetting<MovementColumnId>[];
    kind: MovementKind | null;
  };
  preferences: {
    theme: "auto" | "light" | "dark";
    colorUnit: ColorUnit;
    sidebarCollapsed: boolean;
  };
}

export interface NamedLayout {
  id: string;
  name: string;
  savedAt: string;
  layout: Layout;
}

// Category is off by default because rows are grouped by category by default.
const hidden: InventoryColumnId[] = [
  "category",
  "sku",
  "value",
  "lots",
  "manufacturer",
  "color",
  "alpha",
];

const hiddenRecipeColumns: RecipeColumnId[] = ["type", "brewer", "version", "updated"];
const narrowRecipeColumns: RecipeColumnId[] = ["name", "og", "abv", "ibu", "srm"];

export function defaultLayout(narrow: boolean): Layout {
  return {
    version: 1,
    inventory: {
      view: narrow ? "cards" : "table",
      columns: INVENTORY_COLUMNS.map((c) => ({
        id: c.id,
        visible: narrow ? ["name", "onHand"].includes(c.id) : !hidden.includes(c.id),
      })),
      sort: { key: "name", dir: "asc" },
      groupByCategory: true,
      quantityMode: "unit",
      category: null,
      locationId: null,
      hideEmpty: false,
      cardSize: narrow ? "small" : "medium",
      collapsed: {},
    },
    recipes: {
      view: "cards",
      columns: RECIPE_COLUMNS.map((c) => ({
        id: c.id,
        visible: narrow ? narrowRecipeColumns.includes(c.id) : !hiddenRecipeColumns.includes(c.id),
      })),
      sort: { key: "name", dir: "asc" },
      groupByStyle: false,
      type: null,
      style: null,
      stock: "all",
      favoritesCollapsed: false,
    },
    schedule: { ahead: 31, custom: false },
    item: { collapsed: { specs: false, lots: false, history: false, locations: false } },
    // A phone shows about two weeks, a desktop a month (Scott's Jam: "a week, a month, six months").
    production: { zoom: narrow ? 14 : 31, showHidden: false, folded: {} },
    movements: {
      columns: MOVEMENT_COLUMNS.map((c) => ({
        id: c.id,
        visible: !["entered", "counterparty", "lot"].includes(c.id) || !narrow,
      })),
      kind: null,
    },
    preferences: { theme: "auto", colorUnit: "lovibond", sidebarCollapsed: false },
  };
}

/**
 * Inventory's Form column shows on a layout saved before it existed, beside the name: it carries what a
 * hop's name used to ("Citra T-90" is Citra, in T-90 pellets; Scott's Jam, 2026-10-02). Region shows
 * beside it the same way, a hop's regions being its lots' (Scott, 2026-10-02).
 */
const SHOWN_WHEN_NEW: readonly string[] = ["form", "region"];

/**
 * Keeps saved column order, drops columns that no longer exist, and appends new ones hidden, but for
 * one of `SHOWN_WHEN_NEW`, which goes where it sits by default, shown as it is by default.
 */
/** Columns saved under a name they no longer have. */
const RENAMED_COLUMNS: Record<string, string> = { free: "available" };

function mergeColumns<Id extends string>(
  saved: unknown,
  defaults: ColumnSetting<Id>[],
): ColumnSetting<Id>[] {
  if (!Array.isArray(saved)) return defaults;
  const known = new Map(defaults.map((c) => [c.id, c]));
  const out: ColumnSetting<Id>[] = [];
  for (const entry of saved) {
    const was = (entry as { id?: unknown })?.id;
    // A column renamed keeps its place and whether it shows on a layout saved under its old name.
    const id = typeof was === "string" ? (RENAMED_COLUMNS[was] ?? was) : was;
    if (typeof id === "string" && known.has(id as Id) && !out.some((c) => c.id === id)) {
      out.push({ id: id as Id, visible: Boolean((entry as { visible?: unknown }).visible) });
    }
  }
  for (const [index, c] of defaults.entries()) {
    if (out.some((o) => o.id === c.id)) continue;
    if (!SHOWN_WHEN_NEW.includes(c.id)) {
      out.push({ ...c, visible: false });
      continue;
    }
    const after = defaults
      .slice(0, index)
      .reverse()
      .find((d) => out.some((o) => o.id === d.id));
    out.splice(after ? out.findIndex((o) => o.id === after.id) + 1 : 0, 0, { ...c });
  }
  return out;
}

function pick<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/**
 * Reads whatever was stored, however old, into a complete Layout. Anything unrecognized falls back to the
 * default, so a layout saved by an older version of the app never breaks a newer one.
 */
export function normalize(raw: unknown, narrow: boolean): Layout {
  const d = defaultLayout(narrow);
  if (!raw || typeof raw !== "object") return d;
  const r = raw as Partial<Record<keyof Layout, Record<string, unknown>>>;
  const inv = r.inventory ?? {};
  const sort = (inv.sort ?? {}) as Record<string, unknown>;
  const mov = r.movements ?? {};
  const rec = r.recipes ?? {};
  const recSort = (rec.sort ?? {}) as Record<string, unknown>;
  const prefs = r.preferences ?? {};
  const sched = r.schedule ?? {};
  const days = (value: unknown, fallback: number) =>
    typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 3660
      ? value
      : fallback;
  const collapsed = (r.item?.collapsed ?? {}) as Record<string, unknown>;
  const folded = (inv.collapsed ?? {}) as Record<string, unknown>;
  const prod = r.production ?? {};
  const kinds = (prod.folded ?? {}) as Record<string, unknown>;
  const flags = (o: Record<string, unknown>) =>
    Object.fromEntries(
      Object.entries(o).filter((e): e is [string, boolean] => typeof e[1] === "boolean"),
    );
  return {
    version: 1,
    inventory: {
      view: pick(inv.view, ["table", "cards", "overview"] as const, d.inventory.view),
      columns: mergeColumns(inv.columns, d.inventory.columns),
      sort: {
        key: pick(sort.key, ["name", "category", "onHand", "lastMovement"] as const, "name"),
        dir: pick(sort.dir, ["asc", "desc"] as const, "asc"),
      },
      groupByCategory:
        typeof inv.groupByCategory === "boolean"
          ? inv.groupByCategory
          : d.inventory.groupByCategory,
      quantityMode: pick(inv.quantityMode, ["unit", "pack"] as const, "unit"),
      category: typeof inv.category === "string" ? inv.category : null,
      locationId: typeof inv.locationId === "string" ? inv.locationId : null,
      hideEmpty: typeof inv.hideEmpty === "boolean" ? inv.hideEmpty : false,
      cardSize: pick(inv.cardSize, ["small", "medium", "large"] as const, d.inventory.cardSize),
      collapsed: flags(folded),
    },
    recipes: {
      view: pick(rec.view, ["cards", "list", "table"] as const, d.recipes.view),
      columns: mergeColumns(rec.columns, d.recipes.columns),
      sort: {
        key: pick(recSort.key, RECIPE_SORT_KEYS, "name"),
        dir: pick(recSort.dir, ["asc", "desc"] as const, "asc"),
      },
      groupByStyle: typeof rec.groupByStyle === "boolean" ? rec.groupByStyle : false,
      type: typeof rec.type === "string" ? rec.type : null,
      style: typeof rec.style === "string" ? rec.style : null,
      stock: pick(rec.stock, ["all", "ready", "short", "unlinked"] as const, "all"),
      favoritesCollapsed:
        typeof rec.favoritesCollapsed === "boolean" ? rec.favoritesCollapsed : false,
    },
    schedule: {
      ahead: days(sched.ahead, d.schedule.ahead),
      custom: typeof sched.custom === "boolean" ? sched.custom : false,
    },
    item: {
      collapsed: Object.fromEntries(
        Object.entries({ ...d.item.collapsed, ...collapsed }).map(([k, v]) => [k, Boolean(v)]),
      ),
    },
    production: {
      zoom: pick(prod.zoom, TANK_ZOOMS, d.production.zoom),
      showHidden: typeof prod.showHidden === "boolean" ? prod.showHidden : false,
      folded: flags(kinds),
    },
    movements: {
      columns: mergeColumns(mov.columns, d.movements.columns),
      // A kind the log no longer has, kept from an older build, is no filter at all.
      kind: pick<MovementKind | null>(mov.kind, MOVEMENT_KINDS, null),
    },
    preferences: {
      theme: pick(prefs.theme, ["auto", "light", "dark"] as const, "auto"),
      colorUnit: pick(prefs.colorUnit, ["lovibond", "srm", "ebc"] as const, "lovibond"),
      sidebarCollapsed:
        typeof prefs.sidebarCollapsed === "boolean" ? prefs.sidebarCollapsed : false,
    },
  };
}

const PREFIX = "bl:v1:";
export const KEYS = {
  current: `${PREFIX}layout`,
  saved: `${PREFIX}layouts`,
  active: `${PREFIX}activeLayout`,
};

/** Storage can be missing or refuse writes (private windows, blocked site data); the app still works. */
export function readStorage(key: string): unknown {
  try {
    const text = window.localStorage.getItem(key);
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

export function writeStorage(key: string, value: unknown): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not persisted on this device; the in-memory layout still applies for this visit.
  }
}

export function readSaved(narrow: boolean): NamedLayout[] {
  const raw = readStorage(KEYS.saved);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e) => e && typeof e.id === "string" && typeof e.name === "string")
    .map((e) => ({
      id: e.id,
      name: e.name,
      savedAt: typeof e.savedAt === "string" ? e.savedAt : new Date(0).toISOString(),
      layout: normalize(e.layout, narrow),
    }));
}

export function isNarrow(): boolean {
  try {
    return window.matchMedia("(max-width: 760px)").matches;
  } catch {
    return false;
  }
}
